import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import {
  TicketsPipelineDrawerService,
  TicketsPipelineStageDrawerService
} from '../../../services/tickets-config-drawer.services';
import {
  TicketPipeline,
  TicketPipelineStage,
  TicketStatus
} from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-pipelines-detail',
  standalone: true,
  imports: [CommonModule, PageBreadcrumbComponent],
  templateUrl: './pipelines-detail.component.html'
})
export class TicketsPipelinesDetailComponent implements OnInit, OnDestroy {
  pipeline: TicketPipeline | null = null;
  stages: TicketPipelineStage[] = [];
  statuses: TicketStatus[] = [];
  loading = false;
  error = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    protected router: Router,
    private configApi: TicketsConfigApiService,
    private pipelineDrawer: TicketsPipelineDrawerService,
    private stageDrawer: TicketsPipelineStageDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) return;
    this.cargar(id);

    // Refrescar al guardar pipeline o stage
    this.pipelineDrawer.entitySaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((p) => {
        if (p.id === this.pipeline?.id) this.pipeline = p;
      });
    this.stageDrawer.entitySaved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargarStages());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      pipeline: this.configApi.getPipelineById(id),
      stages: this.configApi.getPipelineStages(id).pipe(catchError(() => of([] as TicketPipelineStage[]))),
      statuses: this.configApi.getStatuses().pipe(catchError(() => of([] as TicketStatus[])))
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ pipeline, stages, statuses }) => {
        this.pipeline = pipeline;
        this.stages = stages.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        this.statuses = statuses;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el pipeline';
        this.loading = false;
      }
    });
  }

  private cargarStages(): void {
    if (!this.pipeline?.id) return;
    this.configApi.getPipelineStages(this.pipeline.id)
      .pipe(catchError(() => of([] as TicketPipelineStage[])), takeUntil(this.destroy$))
      .subscribe((s) => {
        this.stages = s.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      });
  }

  // ===== Acciones =====
  editarPipeline(): void {
    if (this.pipeline) this.pipelineDrawer.open({ id: this.pipeline.id });
  }

  nuevaEtapa(): void {
    if (this.pipeline) this.stageDrawer.open({ parentId: this.pipeline.id });
  }

  editarEtapa(stage: TicketPipelineStage): void {
    this.stageDrawer.open({ id: stage.id });
  }

  async eliminarEtapa(stage: TicketPipelineStage): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar etapa',
      message: `¿Eliminar la etapa "${stage.name}"?`
    });
    if (!ok) return;
    this.configApi.deletePipelineStage(stage.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.toast.deleted('Etapa', `Se eliminó "${stage.name}" correctamente.`); this.cargarStages(); },
      error: (err) => this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo eliminar' })
    });
  }

  volver(): void { this.router.navigate(['/tickets/config/pipelines']); }

  getStatusLabel(idStatus: number | null): string {
    if (idStatus == null) return '—';
    return this.statuses.find((s) => s.id === idStatus)?.label || `#${idStatus}`;
  }
}
